import React from 'react';
import { Formik, useFormikContext } from 'formik';
import { Box, Chip, Typography } from '@mui/material';
import { updatePatientMedications } from '@uplift/api';
import { usePatientMedicationQuery } from '@uplift/queries';
import { PsychiatryNoteSection } from '../components/PsychiatryNoteSection.component';
import { YesNoRadioButtonGroup } from '../../../Inputs';
import { FormikAutoSave } from '../../../ServerForm';
import { FloatingActionBar } from '../components/FloatingActionBar.component';
import { MedicationsForm } from '../../ProviderPatientProfile/MedicationsForm/MedicationsForm.component';
import { Button } from '../../../Button';


export const CurrentMedicationSectionForm: React.VFC<{ autoSave?: boolean }> = ({ autoSave = true }) => {
  const formik = useFormikContext<{ isDrugs: boolean }>();

  return (
    <>
      <Box px={3} pb={2}>
        <YesNoRadioButtonGroup size="small" label="Is the client taking any medication?" name="isDrugs" asBoolean />
      </Box>
      {formik.values.isDrugs ? (
        <Box px={3} py={2} bgcolor="grey.100">
          <MedicationsForm />
        </Box>
      ) : null}
      {autoSave ? <FormikAutoSave /> : null}
    </>
  );
};


export const CurrentMedicationSectionFormik: React.FC<{ patientId: string; onSuccess?: () => void }> = ({ patientId, onSuccess, children }) => {
  const { data: medicationData, refetch } = usePatientMedicationQuery(patientId, { suspense: true });
  const hasMedications = (medicationData?.homemeds?.length ?? 0) > 0;

  return (
    <Formik
      initialValues={hasMedications ? {
        isDrugs: hasMedications,
        drugs: medicationData?.homemeds?.map(d => d.drugName) ?? [],
      } : {
        isDrugs: null,
        drugs: [],
      }}
      onSubmit={async (values) => {
        const drugs = values.isDrugs ? values.drugs : [];

        await updatePatientMedications(patientId, drugs);
        await refetch();
        onSuccess?.();
      }}
    >
      {children}
    </Formik>
  );
};


export const CurrentMedicationEditSection: React.VFC<{ patientId: string }> = ({ patientId }) => {
  return (
    <PsychiatryNoteSection title="Current Medication">
      <CurrentMedicationSectionFormik patientId={patientId}>
        <CurrentMedicationSectionForm />
      </CurrentMedicationSectionFormik>
    </PsychiatryNoteSection>
  );
};


export const CurrentMedicationViewSection: React.VFC<{ patientId: string; onEdit?: () => void }> = ({ patientId, onEdit }) => {
  const { data: medicationData } = usePatientMedicationQuery(patientId, { suspense: true });

  return (
    <PsychiatryNoteSection
      title="Current Medication"
      actions={onEdit && (
        <Button
          onClick={onEdit}
          size="small"
          color="secondary"
        >
          Edit
        </Button>
      )}
    >
      <Box px={3} display="flex" gap="16px">
        {medicationData?.homemeds?.length ? medicationData?.homemeds.map(h => (
          <Chip
            key={h.drugName}
            label={h.drugName}
            variant="outlined"
            style={{ borderRadius: 'unset' }}
          />
        )) : (
          <Typography>No medications on record</Typography>
        )}
      </Box>
    </PsychiatryNoteSection>
  );
};


export const CurrentMedicationSection: React.VFC<{ patientId: string }> = ({ patientId }) => {
  const [ isEditing, setIsEditing ] = React.useState(false);

  if (isEditing) {
    return (
      <CurrentMedicationSectionFormik patientId={patientId} onSuccess={() => setIsEditing(false)}>
        <>
          <PsychiatryNoteSection title="Current Medication">
            <CurrentMedicationSectionForm autoSave={false} />
          </PsychiatryNoteSection>
          <FloatingActionBar onCancel={() => setIsEditing(false)} />
        </>
      </CurrentMedicationSectionFormik>
    );
  }

  return (
    <CurrentMedicationViewSection patientId={patientId} onEdit={() => setIsEditing(true)} />
  );
};
